// Phase 04 seam hook for Similarity (04j-a) — the Phase-02-deferred
// useSimilarityCandidates, landed here with its engine (the similarity algorithm), the same way
// useRankings/useFilterResults landed with theirs. A thin useMemo over the pure
// similarity-view.ts scoring layer + the snapshot population.
//
// The widget reads `useSimilaritySeason` + `useSimilarityValues` (two steps, as the Radar's — the
// template sits between them): the snapshot path is the hooks it always ran; with ?live=1 the same
// shapes come from her league's Stats file (`live/similarity.ts`), picked once at load (`LIVE`).

import { chipIsOn } from "../engines/stats-filter-kit.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import type { OptionInfo } from "../modules/FilterPopovers.js";
import { groupsKey, playersOfGroups, useLiveLeagueGroups } from "./useLeagueGroups.js";
import { useLiveLeagueChoices } from "./useLeagueTable.js";
import { chipsWithData } from "../live/stats-files.js";
import { useMemo } from "react";
import type { RawPlayerRecord } from "../data/snapshot.js";
import { readSnapshot } from "../data/snapshot.js";
import { playerById, type PlayerId } from "../data/players.js";
import { similarityResultsFor, SIMILARITY_RANGE_FILTERS, type SimilarityResult } from "../modules/similarity-view.js";
import type { RadarAxis, RadarRange } from "../modules/radar-view.js";
import type { ActiveFilters, CategoricalActive, KitPlayer, RangeActive, RangeFilterConfig } from "../engines/stats-filter-kit.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { useRadarRanges } from "./useRadarRanges.js";
import { useLiveGroupFile, type LiveGroupFile } from "./useGroupFile.js";
import { LIVE } from "../live/flag.js";
import { groupChipOptions, groupPlayers } from "../live/group-players.js";
import { herTemplate, liveRadarRanges, templatePopulation } from "../live/radar.js";
import { rememberWomens } from "../live/player-gender.js";
import { labelPositionRows } from "../live/comparison.js";
import { MINUTES_CHIP_DEFAULT } from "../live/stats-files.js";
import {
  liveSimilarityResults,
  LIVE_SIMILARITY_CHIPS,
  LIVE_SIMILARITY_RANGE_FILTERS,
  playerRecord,
  similarityMetricKeys,
} from "../live/similarity.js";

export interface UseSimilarityCandidatesParams {
  base: RawPlayerRecord | null;
  baseSeason: string;
  axisKeys: readonly string[];
  activeFilters: ActiveFilters;
  rangeFilters?: RangeFilterConfig;
}

/** The ranked similar-candidate list for a base player over the current axes/season/filters.
 *  `axisKeys` and `activeFilters` are depended on BY REFERENCE — the renderer memoises the axis
 *  list and hands a fresh clone of the filters per version-bump (the chip-bar bridge). */
export function useSimilarityCandidates({
  base,
  baseSeason,
  axisKeys,
  activeFilters,
  rangeFilters,
}: UseSimilarityCandidatesParams): SimilarityResult[] {
  return useMemo(() => {
    if (!base) return [];
    return similarityResultsFor({ players: readSnapshot().players, base, baseSeason, axisKeys, activeFilters, rangeFilters });
  }, [base, baseSeason, axisKeys, activeFilters, rangeFilters]);
}

// --- the widget's two steps (snapshot or live) -----------------------------------------

/** The overlay as the widget stores it (`_similarityOverlayCandidate`). `position` is live only:
 *  a row clicked with a Position chip on overlays her minutes at that position. */
export interface SimilarityOverlay {
  id: PlayerId;
  season: string | null;
  position?: string | null;
}

/** What the chip bar needs (`FilterChipBar`'s props). The optional ones are live only. */
export interface SimilarityChips {
  /** Live only: each value's heading + icon in its list (the League chip: country + flag). */
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
  options: Record<string, string[]>;
  refPlayer: KitPlayer;
  rangeFilters: RangeFilterConfig;
  addMenuKeys?: readonly string[];
  newChipValue?: (key: string) => CategoricalActive | RangeActive | undefined;
}

/** Step 1 — the player's side: which season, and the template a fresh tile opens on. */
export interface SimilaritySeason {
  /** The season picker's list, newest first (live: her league's built seasons). */
  seasonCodes: string[];
  season: string;
  /** Her usual position (live: most minutes in this file); "" = none known. */
  defaultTemplate: string;
  /** Live: no file for the season shown → why ("Premier League 22/23 isn't built yet"). */
  noGroup: string | null;
  chips: SimilarityChips;
  /** Her gender (a league's file is one gender) — a dragged row notes it, so a live Stats / Radar
   *  tile can take her (T2-14b). Null = no file shown. */
  womens: boolean | null;
  src: { kind: "snapshot"; base: RawPlayerRecord | null } | { kind: "live"; group: LiveGroupFile; playerId: PlayerId };
  /** Live only: the League chip's default — her league (Ben, 2026-10-16). */
  defaultLeague?: string;
}

/** Step 2 — her record, the ranked list, the spokes' scale, the overlay. */
export interface SimilarityValues {
  /** Her record for the season shown; null = no data (live: no row in the file). */
  base: RawPlayerRecord | null;
  results: SimilarityResult[];
  /** Each spoke's scale (the chart's), keyed by axis. */
  ranges: Map<string, RadarRange>;
  /** Who the report's per-metric ranges come from (Ben: the whole population, never the chips). */
  rangePopulation: Record<string, RawPlayerRecord>;
  /** The overlaid player's record + the season it's for; null = none (or no data for her). */
  overlay: { record: RawPlayerRecord; season: string } | null;
}

function useSnapshotSimilaritySeason(name: string, _id: PlayerId | null, requested: string | undefined): SimilaritySeason {
  const players = readSnapshot().players;
  const base = players[name] ?? null;
  const options = useFilterOptions();
  const refPlayer = useMemo<KitPlayer>(() => (base as unknown as KitPlayer) ?? ({} as KitPlayer), [base]);
  const season = base && requested && base.seasons?.[requested] ? requested : (base?.currentSeason ?? "");
  return {
    seasonCodes: Object.keys(base?.seasons ?? {}).sort((a, b) => b.localeCompare(a)),
    season,
    defaultTemplate: base?.positionTemplate ?? "",
    noGroup: null,
    chips: { options, refPlayer, rangeFilters: SIMILARITY_RANGE_FILTERS },
    // The snapshot holds women's leagues only (and the snapshot site has no gender rule).
    womens: true,
    src: { kind: "snapshot", base },
  };
}

const NO_REF: KitPlayer = {};
// A League chip added from "+ Add filter" starts with nothing ticked (Ben, 2026-10-16: not the
// player's league, which a first tick would then add to). Minutes starts at 150+ (rules.md).
const liveNewChipValue = (key: string) =>
  key === "league" ? [] : key === "minutes" ? { ...MINUTES_CHIP_DEFAULT } : undefined;

function useLiveSimilaritySeason(_name: string, id: PlayerId | null, requested: string | undefined): SimilaritySeason {
  // Live always has one: the module falls back to the snapshot's first player (a real DB id).
  if (id === null) throw new Error("A live Similarity needs a player");
  const g = useLiveGroupFile(id, requested ?? null);
  const group = g.kind === "file" ? g.group : null;
  const label = g.kind === "file" ? g.league.label : null;
  const teamNow = g.kind === "file" ? g.league.teamName : null;
  // So a drop onto this tile can tell a man from a woman — her league's file is one gender. Same
  // value on every render, so writing it here is safe (as the live Radar).
  if (group) rememberWomens(id, group.file.womens);
  // Her own row (the chips' first values: her main position, club, country). Not in this file
  // (another season, or she left this league) → her club now. As Comparison's.
  const refPlayer = useMemo<KitPlayer>(() => {
    if (!group || label === null) return NO_REF;
    return groupPlayers(group, "total", [], label)[String(id)] ?? { team: teamNow };
  }, [group, label, id, teamNow]);
  // The League chip (Ben, 2026-10-16): which leagues the similar players come from; its list =
  // her gender's leagues. Her own shape and the spokes' scale stay on her league.
  const choices = useLiveLeagueChoices();
  const womens = group ? group.file.womens : null;
  const options = useMemo(
    () => ({ ...(group ? groupChipOptions(group) : {}), league: choices.filter((c) => c.womens === womens).map((c) => c.label) }),
    [group, choices, womens],
  );
  // The picker lists the league's built seasons; the season shown is added if it isn't built,
  // so the picker never shows a blank (as Ranking).
  const season = String(g.seasonId);
  const built = g.seasons.map(String);
  return {
    seasonCodes: built.includes(season) ? built : [season, ...built],
    season,
    defaultTemplate: group ? (herTemplate(group.file, id) ?? "") : "",
    noGroup: g.kind === "none" ? g.label : null,
    chips: {
      options,
      refPlayer,
      rangeFilters: LIVE_SIMILARITY_RANGE_FILTERS,
      // Q-013: only the chips at least half the players (with minutes) have data for.
      addMenuKeys: group ? chipsWithData(LIVE_SIMILARITY_CHIPS, [group.file]) : [],
      newChipValue: liveNewChipValue,
      optionInfo: { league: leagueOptionInfo(choices.filter((c) => c.womens === womens)) },
    },
    womens: group ? group.file.womens : null,
    src: { kind: "live", group: g, playerId: id },
    defaultLeague: label ?? undefined,
  };
}

function useSnapshotSimilarityValues(
  s: SimilaritySeason,
  template: string,
  axes: readonly RadarAxis[],
  axisKeys: readonly string[],
  activeFilters: ActiveFilters,
  overlay: SimilarityOverlay | null,
): SimilarityValues {
  const players = readSnapshot().players;
  const base = s.src.kind === "snapshot" ? s.src.base : null;
  const ranges = useRadarRanges(template, axes);
  const results = useSimilarityCandidates({
    base,
    baseSeason: s.season,
    axisKeys,
    activeFilters,
    rangeFilters: SIMILARITY_RANGE_FILTERS,
  });
  const overlayRecord = overlay ? (playerById(players, overlay.id) ?? null) : null;
  return {
    base,
    results,
    ranges,
    rangePopulation: players,
    // Her season exactly as stored (the old code read `overlay.season` as-is).
    overlay: overlayRecord && overlay ? { record: overlayRecord, season: overlay.season as string } : null,
  };
}

function useLiveSimilarityValues(
  s: SimilaritySeason,
  template: string,
  axes: readonly RadarAxis[],
  axisKeys: readonly string[],
  activeFilters: ActiveFilters,
  overlay: SimilarityOverlay | null,
  /** The user removed the League chip (✕): no candidates until a league is picked. */
  leagueCleared = false,
): SimilarityValues {
  const live = s.src.kind === "live" ? s.src : null;
  if (!live) throw new Error("A live Similarity read a snapshot season");
  const { group: g, playerId } = live;
  // The overlay's own league file (a player dropped from another tile can be from another league
  // or season). With no overlay it asks for the base's own file again — the same keys, so no
  // call; one hook either way (a hook can't come and go).
  const og = useLiveGroupFile(overlay ? overlay.id : playerId, overlay ? overlay.season : s.season);
  // The candidates come from the League chip's leagues (her league by default; tick others).
  const lg = useLiveLeagueGroups(playerId, s.season, activeFilters.league as CategoricalActive | undefined, leagueCleared);
  const lgKey = groupsKey(lg);

  const group = g.kind === "file" ? g.group : null;
  const label = g.kind === "file" ? g.league.label : null;
  const keys = useMemo(() => similarityMetricKeys(axisKeys), [axisKeys]);
  const byPosition = chipIsOn(activeFilters.position);

  const board = useMemo(() => {
    if (!group || label === null) return { base: null, rangePopulation: {}, results: [] as SimilarityResult[] };
    const base = playerRecord(group.file, playerId, null, keys, label);
    if (base) base.positionTemplate = herTemplate(group.file, playerId);
    const rangePopulation = groupPlayers(group, "total", keys, label);
    if (!base) return { base: null, rangePopulation, results: [] as SimilarityResult[] };
    if (lg.problem !== null) return { base, rangePopulation, results: [] as SimilarityResult[] };
    const fromLeagues = (pick: "position" | "total") => playersOfGroups(lg, pick, keys);
    const candidates = byPosition ? labelPositionRows(fromLeagues("position")) : fromLeagues("total");
    const results = liveSimilarityResults({
      candidates,
      rangePopulation,
      base,
      season: s.season,
      axisKeys,
      activeFilters,
      rangeFilters: LIVE_SIMILARITY_RANGE_FILTERS,
    });
    return { base, rangePopulation, results };
  }, [group, label, playerId, keys, byPosition, s.season, axisKeys, activeFilters, lg.problem, lgKey]);

  // The spokes: scaled as the live Radar's — everyone's minutes at the template (Q-009, Q-011 B).
  const ranges = useMemo(
    () => liveRadarRanges(group && label !== null ? templatePopulation(group, template, axes, label) : [], template, axes),
    [group, label, template, axes],
  );

  const overlayFile = og.kind === "file" ? og.group.file : null;
  const overlayLabel = og.kind === "file" ? og.league.label : null;
  const overlayRecord = useMemo(() => {
    if (!overlay || !overlayFile || overlayLabel === null) return null;
    // The drop rules only let her in when she's the base's gender; two genders here is a bug.
    if (group && overlayFile.womens !== group.file.womens) {
      throw new Error(`Similarity: overlay ${overlay.id} and base ${playerId} are a man and a woman`);
    }
    const record = playerRecord(overlayFile, overlay.id, overlay.position ?? null, keys, overlayLabel);
    return record ? { record, season: String(overlayFile.seasonId) } : null;
  }, [overlay, overlayFile, overlayLabel, group, playerId, keys]);

  return { base: board.base, results: board.results, ranges, rangePopulation: board.rangePopulation, overlay: overlayRecord };
}

export const useSimilaritySeason: (name: string, id: PlayerId | null, requested: string | undefined) => SimilaritySeason = LIVE
  ? useLiveSimilaritySeason
  : useSnapshotSimilaritySeason;

export const useSimilarityValues: (
  s: SimilaritySeason,
  template: string,
  axes: readonly RadarAxis[],
  axisKeys: readonly string[],
  activeFilters: ActiveFilters,
  overlay: SimilarityOverlay | null,
  leagueCleared?: boolean,
) => SimilarityValues = LIVE ? useLiveSimilarityValues : useSnapshotSimilarityValues;

